import Link from '@tiptap/extension-link';
import type { MentionOptions } from '@tiptap/extension-mention';
import StarterKit from '@tiptap/starter-kit';

// Shared Tiptap building blocks for candidate notes. Native and PeopleForce
// notes are both persisted as HTML (the composer saves `editor.getHTML()`), so
// the read-only feed (`candidate-note-body`) and the edit modal
// (`candidate-note-edit-dialog`) must render that body through the same
// extensions or the round-trip drifts. The composer keeps its own base (it
// suppresses headings in the UI), but reuses the mention render below.

export const MENTION_HTML_ATTRIBUTES = {
  class: 'mention rounded-sm bg-hygge/15 px-1 font-medium text-hygge',
};

// Mentions are stored as `<span data-id data-label>@Label</span>`. Every surface
// (composer, feed, edit) renders them identically so a mention looks the same
// whether it's being authored, displayed, or edited.
export const mentionRenderHTML: NonNullable<MentionOptions['renderHTML']> = ({ options, node }) => [
  'span',
  { ...options.HTMLAttributes, 'data-id': node.attrs.id, 'data-label': node.attrs.label },
  `@${node.attrs.label ?? node.attrs.id}`,
];

/**
 * Base extensions for rendering/editing a stored note body. Headings 2-3 are
 * kept so PeopleForce "Candidate Analysis" reports preserve their section
 * structure. `openOnClick` differs by surface: the read-only feed opens links on
 * click, the editable modal positions the caret instead.
 */
export function candidateNoteBaseExtensions({ openOnClick }: { openOnClick: boolean }) {
  return [
    // StarterKit v3 bundles the Link extension, so disable it here and let the
    // configured Link below be the only `link` — a second one triggers TipTap's
    // "Duplicate extension names" warning.
    StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
    Link.configure({
      openOnClick,
      autolink: true,
      HTMLAttributes: { class: 'text-hygge underline' },
    }),
  ];
}
